import { Badge } from "@components/ui/badge";
import { useBillingWorkspace } from "../hooks/useBillingWorkspace";
import { useWorkspaceBilling } from "../hooks/useWorkspaceBilling";
import { Check, CreditCard, LoaderCircle } from "lucide-react";
import { BillingAccessSummary } from "../components/billing/BillingAccessSummary";
import type { BillingStatus } from "@io-spoke/contracts/billing";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";

type BillingPageProps = { navigate: (route: string) => void };

const statusCopy: Record<BillingStatus["status"], string> = {
  trialing: "Free trial",
  active: "Active",
  expired: "Trial ended",
  past_due: "Payment needed",
  canceled: "Canceled",
};

function BillingPaymentAction({ billing, paying, checkout, portal }: {
  billing: BillingStatus; paying: boolean; checkout: () => Promise<void>; portal: () => Promise<void>;
}) {
  if (billing.status === "active") {
    if (billing.paymentProcessor !== "stripe") return null;
    return <Button loading={paying} loadingLabel="Opening subscription…" onClick={portal}><CreditCard className="mr-2 h-4 w-4" />Manage subscription</Button>;
  }
  return <Button loading={paying} loadingLabel="Opening checkout…" onClick={checkout}><CreditCard className="mr-2 h-4 w-4" />Add payment</Button>;
}

function BillingContent({ loading, billing, error, paying, checkout, portal }: {
  loading: boolean; billing: BillingStatus | null; error: string | null;
  paying: boolean; checkout: () => Promise<void>; portal: () => Promise<void>;
}) {
  if (loading) return (<div className="mt-10 flex items-center gap-3 text-sm text-muted-foreground" role="status"><LoaderCircle className="h-5 w-5 animate-spin" /> Loading billing status</div>);
  if (!billing) return (<Card className="mt-8 border-destructive/40"><CardContent className="pt-6"><p role="alert" className="text-sm text-destructive">{error ?? "Billing status is unavailable."}</p><Button className="mt-4" variant="outline" onClick={() => window.location.reload()}>Try again</Button></CardContent></Card>);
  return (
<div className="mt-8 grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
          <Card className="overflow-hidden border-border bg-card/60">
            <CardHeader className="border-b border-border bg-muted/20">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div><p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">IO Spoke Standard</p><CardTitle className="mt-2 text-2xl">{statusCopy[billing.status]}</CardTitle></div>
                <Badge className={billing.access === "full" ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300" : "border-amber-500/30 bg-amber-500/15 text-amber-900 dark:text-amber-300"}>{billing.access === "full" ? "Full access" : "Read only"}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-7 pt-6">
              <BillingAccessSummary billing={billing} />

              <div className="flex items-end justify-between gap-5 border-t border-border pt-6"><div><p className="text-3xl font-semibold">$299<span className="text-base font-normal text-muted-foreground"> / month</span></p><p className="mt-1 text-sm text-muted-foreground">Per workspace. Unlimited teammates.</p></div><BillingPaymentAction billing={billing} paying={paying} checkout={checkout} portal={portal} /></div>
              {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
            </CardContent>
          </Card>

          <Card className="border-border bg-card/40"><CardHeader><CardTitle className="text-lg">Included</CardTitle></CardHeader><CardContent><ul className="space-y-4 text-sm">{["Customer risk monitoring", "Evidence-backed rescue plans", "Unlimited teammates", "One connected workspace"].map((item) => <li className="flex gap-3" key={item}><Check className="h-4 w-4 text-emerald-400" aria-hidden="true" /><span>{item}</span></li>)}</ul></CardContent></Card>
        </div>
  );
}

export function BillingPage({ navigate }: BillingPageProps) {
  const workspace = useBillingWorkspace();
  const { billing, error: billingError, loading, checkout, portal, paying } = useWorkspaceBilling(workspace);
  const error = workspace.error ?? billingError;
  const isLoading = !error && (!workspace.ready || loading);

  const breadcrumb = (
    <Breadcrumb><BreadcrumbList><BreadcrumbItem><BreadcrumbLink asChild><button type="button" onClick={() => navigate("/dashboard")}>Dashboard</button></BreadcrumbLink></BreadcrumbItem><BreadcrumbSeparator /><BreadcrumbItem><BreadcrumbPage>Billing</BreadcrumbPage></BreadcrumbItem></BreadcrumbList></Breadcrumb>
  );

  return (
    <PageShell>
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Account"
        title="Plan & billing"
        description={workspace.ready ? `Billing for ${workspace.workspaceName}. Your trial and subscription belong to this workspace.` : "Open the intended workspace before viewing or changing its subscription."}
        actions={<Button variant="outline" onClick={() => navigate("/settings")}>Account settings</Button>}
      />

      <BillingContent loading={isLoading} billing={billing} error={error} paying={paying} checkout={checkout} portal={portal} />
    </PageShell>
  );
}
